import { useInfiniteQuery } from '@tanstack/react-query';
import { findActionIn, invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
const DEFAULT_PAGE_SIZE = 50;
/**
 * Paginated list of projects currently assigned to a given agent. Reads the
 * per-record `listAssignedDLProjects` action from the agent's `potentialAction`.
 * The action's URL template needs the agent's full `id` URI as `object`.
 */
export function useListAssignedDLProjects({ agent, searchTerm, status }) {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const action = findActionIn(agent.potentialAction, 'listAssignedDLProjects');
    const agentUUID = agent.UUID || '';
    const agentIdURI = agent.id || '';
    const sendSearch = searchTerm.length >= 2 ? searchTerm : '';
    const sendStatus = status === 'all' ? '' : status;
    return useInfiniteQuery({
        queryKey: ['listAssignedDLProjects', user?.email, agentUUID, sendSearch, sendStatus],
        enabled: Boolean(user && action && agentIdURI),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const view = lastPage.response.filteredView;
            const total = view?.numberOfItems ?? 0;
            const pageSize = view?.pageSize ?? DEFAULT_PAGE_SIZE;
            const current = view?.currentPage ?? lastPage.page;
            if (current * pageSize >= total)
                return undefined;
            return current + 1;
        },
        queryFn: async ({ pageParam }) => {
            if (!user || !action)
                throw new Error('listAssignedDLProjects unavailable');
            const variables = {
                object: agentIdURI,
                currentPage: String(pageParam),
                pageSize: String(DEFAULT_PAGE_SIZE),
            };
            if (sendSearch)
                variables.searchTerm = sendSearch;
            if (sendStatus)
                variables.status = sendStatus;
            const response = await invokeAction(action, user.token, { variables });
            if (!response.ok)
                throw new Error(`listAssignedDLProjects returned ${response.status}`);
            const json = (await response.json());
            return { response: json, page: pageParam };
        },
    });
}
